import PropertyController from "../../controller/property.controller";
import { GetServerSideProps } from "next";
import React, { Suspense, useCallback, useEffect, useState } from "react";
import dynamic from "next/dynamic";
import Head from "next/head";

import Steps from "../../components/Auth/Steps";
import VerificationSteps from "../../components/VerificationSteps";
import { apiClient } from "../../utils/apiClient";
import { Response } from "../../types/Response.interface";
import { useRouter } from "next/router";
import useStore from "../../context/useStore";

const ImageSlider = dynamic(
  () => import("../../components/Listing/PropertyDetails/ImageSlider"),
  {
    suspense: true,
  }
);

const Details = dynamic(
  () => import("../../components/Listing/PropertyDetails/Details"),
  {
    suspense: true,
  }
);

const Basic = dynamic(
  () => import("../../components/Listing/PropertyDetails/Basic"),
  {
    suspense: true,
  }
);

const Contact = dynamic(
  () => import("../../components/Listing/PropertyDetails/Contact"),
  {
    suspense: true,
  }
);
const SimilarProp = dynamic(
  () => import("../../components/Listing/PropertyDetails/SimilarProp"),
  {
    suspense: true,
  }
);

const PropertyDetail = (props: any) => {
  const router = useRouter();
  const { auth, setDialog } = useStore();

  const [step, setStep] = useState(1);

  const onClick = async () => {
    const res: Response = await apiClient.post("/api/v1/auth/email-verify");
    if (res.ok) {
      setStep(2);
    }
  };

  const track = useCallback(async () => {
    await apiClient.post("/api/v1/track/property", {
      url: "/propertydetails/" + router.query.property,
      price: props.prop.ListPrice,
      mls: props.prop.ListingKey,
      media:
        props.prop.Media.length === 0
          ? "/images/fallback.webp"
          : props.prop.Media[0].MediaURL,
    });
  }, [router.query.property]);

  useEffect(() => {
    if (auth.login) {
      if (auth.role === "user") {
        track();
      }
    }
  }, [auth.login, auth.role, track]);

  return (
    <>
      <Head>
        <title>{`${props.prop.UnparsedAddress?props.prop.UnparsedAddress:props.prop.StreetNumber+" "+props.prop.StreetName+" "+props.prop.StreetSuffix+" "+props.prop.City} - BungalowFinder `}</title>
        <meta name="title" content={`${props.prop.UnparsedAddress?props.prop.UnparsedAddress:props.prop.StreetNumber+" "+props.prop.StreetName+" "+props.prop.StreetSuffix+" "+props.prop.City} - BungalowFinder`} />
        <meta
          name="description"
          content={
            props.prop.PublicRemarks
              ? props.prop.PublicRemarks.replace(/"/g, "").slice(0, 160)
              : `${props.prop.UnparsedAddress?props.prop.UnparsedAddress:props.prop.StreetNumber+" "+props.prop.StreetName+" "+props.prop.StreetSuffix+" "+props.prop.City} is a beautiful luxury Bungalow for Sale in ${props.prop.City}. It contains ${props.prop.BedroomsTotal} Bedrooms,${props.prop.BathroomsTotalInteger} bathrooms,${props.prop.ParkingTotal} parking spaces`
          }
        />

        <meta
          property="og:url"
          content={`https://bungalowfinder.ca/propertydetails/${props.prop.slug}`}
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content={`${props.prop.UnparsedAddress?props.prop.UnparsedAddress:props.prop.StreetNumber+" "+props.prop.StreetName+" "+props.prop.StreetSuffix+" "+props.prop.City} - BungalowFinder`}
        />
        <meta
          property="og:description"
          content={
            props.prop.PublicRemarks
              ? props.prop.PublicRemarks.slice(0, 160)
              : `${props.prop.UnparsedAddress?props.prop.UnparsedAddress:props.prop.StreetNumber+" "+props.prop.StreetName+" "+props.prop.StreetSuffix+" "+props.prop.City} is a beautiful luxury Bungalow for Sale in ${props.prop.City}. It contains ${props.prop.BedroomsTotal} Bedrooms,${props.prop.BathroomsTotalInteger} bathrooms,${props.prop.Tot_park_spcs}`
          }
        />

        <meta
          property="og:image"
          itemProp="image"
          content={
            props.prop._type === "rets"
              ? `https://api.bungalowfinder.ca/cdn/images/${props.prop.ListingKey}/photo_1.png`
              : ""
          }
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta
          property="og:image:alt"
          content={`${props.prop.UnparsedAddress?props.prop.UnparsedAddress:props.prop.StreetNumber+" "+props.prop.StreetName+", "+props.prop.City} - BungalowFinder`}
        />
        <meta name="twitter:card" content="summary" />
        <meta
          name="twitter:title"
          content={`${props.prop.UnparsedAddress?props.prop.UnparsedAddress:props.prop.StreetNumber+" "+props.prop.StreetName+", "+props.prop.City} - BungalowFinder`}
        />
        <meta
          name="twitter:description"
          content={
            props.prop.PublicRemarks
              ? props.prop.PublicRemarks.slice(0, 160)
              : `${props.prop.UnparsedAddress?props.prop.UnparsedAddress:props.prop.StreetNumber+" "+props.prop.StreetName+", "+props.prop.City} is a beautiful luxury Bungalow for Sale in ${props.prop.City}. It contains ${props.prop.BedroomsTotal} Bedrooms,${props.prop.BathroomsTotalInteger} bathrooms,${props.prop.ParkingTotal} parking spaces`
          }
        />
        <meta
          name="twitter:image"
          content="https://BungalowFinder.ca/images/ogimg.jpg"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: `[{
              "@context": "https://schema.org",
              "@type": "RealEstateListing",
              "url": "https://bungalowfinder.ca/propertydetails/${
                props.prop.slug
              }",
              "name": "${props.prop.BedroomsTotal} Bedrooms Bungalow for sale in ${
              props.prop.City
            }",
              "description": "${props.prop.PublicRemarks.replaceAll(
                /(?:\r\n|\r|\n)/g,
                " "
              )
                .replace(/\\/g, " ")
                .replace(/"/g, " ")}",
              "image": "${
                props.prop._type === "rets"
                  ? `https://api.bungalowfinder.ca/cdn/images/${props.prop.ListingKey}/photo_1.png`
               :""
              }",
              "address": {
                "@type": "PostalAddress",
                "streetAddress":"${props.prop.UnparsedAddress?props.prop.UnparsedAddress:props.prop.StreetNumber+" "+props.prop.StreetName+" "+props.prop.StreetSuffix}" ,
                "addressLocality":"${props.prop.City}" ,
                "addressRegion": "Ontario",
                "postalCode": "${props.prop.PostalCode}",
                "addressCountry": "CA"
              },
              "geo": {
                "@type": "GeoCoordinates",
                "latitude": ${props.prop.Lat},
                "longitude": ${props.prop.Lng}
              },
              "price": "$${props.prop.ListPrice}",
              "priceCurrency": "CAD",
              "propertyType": "Bungalow",
              "numberOfBedrooms": ${props.prop.BedroomsTotal},
              "numberOfBathrooms": ${props.prop.BathroomsTotalInteger},
              "availability": "https://schema.org/InStock",
              "agent": {
                "@type": "Person",
                "name": "Tariq Janjua",
                "telephone": "416-828-7773",
                "email": "info@bungalowfinder.ca"
              }
            }]`,
          }}
        ></script>
      </Head>
      <section className={`relative w-full h-full`}>
        {auth.login !== true && (
          <div className="absolute w-full items-center flex justify-center top-20 z-20">
            <div className="flex justify-center bg-white md:px-10 md:py-8 items-center drop-shadow-xl ">
              <div className="md:flex hidden">
                <Steps />
              </div>
              <div className="flex flex-col items-center gap-4 p-3 md:hidden">
                <h3 className="text-lg font-normal">
                  Please Sign Up To View Property
                </h3>
                <button
                  onClick={() => {
                    setDialog({
                      open: true,
                      closable: true,
                    });
                  }}
                  className="bg-primary text-white w-fit px-4 py-2"
                >
                  Sign Up
                </button>
              </div>
            </div>
          </div>
        )}
        {auth.login === true && auth.verify === false && props.prop.Lock && (
          <div className="absolute w-full items-center flex justify-center top-20 z-20">
            <div className="flex justify-center bg-white md:px-10 md:py-8 items-center drop-shadow-xl ">
              <div className="flex flex-col text-center items-center gap-4 max-w-lg">
                <h3 className=" text-base text-primary md:text-lg font-normal">
                  Real Estate Board Requires to Verify This Email to View this
                  Property
                </h3>
                {step === 1 ? (
                  <button
                    onClick={onClick}
                    className="bg-primary text-white py-2 px-4"
                  >
                    Verify Now
                  </button>
                ) : step === 2 ? (
                  <VerificationSteps />
                ) : (
                  <></>
                )}

                {/*  */}
              </div>
            </div>
          </div>
        )}
        <div>
          <Suspense
            fallback={
              <div className="h-[300px] w-full bg-gray-100 animate-pulse" />
            }
          >
            <section className="relative w-full">
              <div
                className={`w-full h-full 
                  ${
                    !auth.login
                      ? "blur"
                      : auth.login && !auth.verify && props.prop.Lock
                      ? "blur"
                      : ""
                  }
                    `}
              >
                <ImageSlider Media={props.prop.Media} key={props.prop.ListingKey} />
              </div>
            </section>
          </Suspense>
          <div className="flex flex-col md:flex-row gap-2 container mx-auto py-10">
            <div className="flex-1 flex flex-col gap-3">
              <Suspense
                fallback={
                  <div className="h-[300px] w-full bg-gray-100 animate-pulse" />
                }
              >
                <Basic data={props.prop} />
                <Details data={props.prop} />
              </Suspense>
            </div>

            <div className="md:top-20 md:sticky h-full">
              <Suspense
                fallback={
                  <div className="h-[300px] w-full bg-gray-100 animate-pulse" />
                }
              >
                <Contact
                  mls={props.prop.ListingKey}
                  address={props.prop.UnparsedAddress?props.prop.UnparsedAddress:props.prop.StreetNumber+" "+props.prop.StreetName+" "+props.prop.StreetSuffix+" "+props.prop.City}
                />
              </Suspense>
            </div>
          </div>
          <div className="py-5">
            <Suspense
              fallback={
                <div className="h-[300px] w-full bg-gray-100 animate-pulse" />
              }
            >
              <SimilarProp similarProp={props.similar} />
            </Suspense>
          </div>
        </div>
      </section>
    </>
  );
};

export default PropertyDetail;

export const getServerSideProps: GetServerSideProps = async (context) => {
  return await PropertyController(context);
  
};
